/**
 * Secure Runtime Configuration Loader
 * 
 * This module provides a secure way to load configuration at runtime
 * rather than embedding it in the build. This prevents sensitive data
 * from being exposed in the client bundle.
 */

class ConfigLoader {
    constructor() {
        this.config = null
        this.isLoaded = false
    }

    /**
     * Load configuration from various sources
     * Priority: 1) Runtime endpoint 2) Local config.json 3) Build-time fallback
     */
    async loadConfig() {
        if (this.isLoaded) {
            return this.config
        }

        try {
            // Only try runtime endpoint in production or if explicitly enabled
            if (process.env.NODE_ENV === 'production' || window.ENABLE_CONFIG_API) {
                this.config = await this.loadFromEndpoint()
            } else {
                // Skip to local config in development
                throw new Error('Skipping runtime endpoint in development')
            }
        } catch (error) {
            // Fallback to local config silently in development
            if (process.env.NODE_ENV !== 'development') {
                console.warn('Failed to load runtime config, falling back to local config:', error.message)
            }
            
            try {
                // Fallback to local config file
                this.config = await this.loadLocalConfig()
            } catch (localError) {
                console.warn('Failed to load local config, using build-time fallback:', localError.message)
                
                // Final fallback to build-time embedded config
                this.config = this.getBuildTimeConfig()
            }
        }

        this.isLoaded = true
        return this.config
    }

    /**
     * Load configuration from a secure runtime endpoint
     * This allows configuration changes without rebuilding
     */
    async loadFromEndpoint() {
        const configEndpoint = this.getConfigEndpoint()
        
        if (!configEndpoint) {
            throw new Error('No runtime config endpoint available')
        }

        const response = await fetch(configEndpoint, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Cache-Control': 'no-cache'
            },
            credentials: 'same-origin' // Only send cookies to same origin
        })

        if (!response.ok) {
            throw new Error(`Config endpoint returned ${response.status}: ${response.statusText}`)
        }

        const config = await response.json()
        
        // Validate the configuration structure
        this.validateConfig(config)
        
        return config
    }

    /**
     * Load configuration from local config.json file
     */
    async loadLocalConfig() {
        const response = await fetch('/config/config.json', {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        })

        if (!response.ok) {
            throw new Error(`Failed to load local config: ${response.statusText}`)
        }

        const config = await response.json()
        this.validateConfig(config)
        return config
    }

    /**
     * Get build-time embedded configuration as final fallback
     */
    getBuildTimeConfig() {
        // These will be replaced by webpack DefinePlugin with safe defaults
        return {
            supabase: {
                url: BUILD_TIME_SUPABASE_URL || '',
                anonKey: BUILD_TIME_SUPABASE_ANON_KEY || ''
            },
            webapi: {
                enabled: true,
                url: BUILD_TIME_WEBAPI_URL || 'http://localhost:3000/api'
            },
            analytics: {
                ga4: {
                    enabled: false,
                    measurementId: ''
                },
                mixpanel: {
                    enabled: false,
                    projectToken: ''
                },
                sentry: {
                    enabled: false,
                    dsn: ''
                }
            },
            development: {
                serverUrl: 'http://localhost',
                worlds: {
                    Login: { port: 6111 },
                    Blizzard: { port: 6112 }
                }
            }
        }
    }

    /**
     * Determine the configuration endpoint URL
     */
    getConfigEndpoint() {
        // Check for configuration endpoint in various places
        const endpoints = [
            window.CONFIG_ENDPOINT,
            document.querySelector('meta[name="config-endpoint"]')?.content,
            process.env.NODE_ENV === 'development' ? '/api/config' : null
        ]

        return endpoints.find(endpoint => endpoint && endpoint.trim() !== '')
    }

    /**
     * Validate configuration structure
     */
    validateConfig(config) {
        if (!config || typeof config !== 'object') {
            throw new Error('Invalid configuration: must be an object')
        }

        // Basic validation - ensure required sections exist
        const requiredSections = ['analytics', 'webapi']
        for (const section of requiredSections) {
            if (!config[section]) {
                throw new Error(`Invalid configuration: missing required section '${section}'`)
            }
        }

        // Validate WebAPI config (required for auth)
        if (!config.webapi.url || typeof config.webapi.url !== 'string') {
            throw new Error('Invalid configuration: webapi.url is required')
        }

        return true
    }

    /**
     * Get specific configuration section
     */
    get(section, defaultValue = null) {
        if (!this.isLoaded) {
            throw new Error('Configuration not loaded. Call loadConfig() first.')
        }

        const keys = section.split('.')
        let value = this.config

        for (const key of keys) {
            if (value && typeof value === 'object' && key in value) {
                value = value[key]
            } else {
                return defaultValue
            }
        }

        return value
    }

    /**
     * Get Supabase configuration
     */
    getSupabaseConfig() {
        return this.get('supabase', {})
    }

    /**
     * Get analytics configuration
     */
    getAnalyticsConfig() {
        return this.get('analytics', {})
    }

    /**
     * Check if feature is enabled
     */
    isFeatureEnabled(feature) {
        return this.get(feature + '.enabled', false)
    }
}

// Create singleton instance
const configLoader = new ConfigLoader()

export default configLoader